import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Trash2, Edit, Plus, Eye, EyeOff, Lock, LogOut, Save, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  
  const [activeTab, setActiveTab] = useState('testimonials');
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    location: '',
    rating: 5,
    text: '',
    service: '',
    approved: true
  });
  
  // FAQs state
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category: 'General',
    order: 0,
    active: true
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setAdminToken(token);
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, { password });
      
      if (response.data.error) {
        toast.error('Invalid password');
        return;
      }
      
      const token = response.data.token;
      setAdminToken(token);
      localStorage.setItem('adminToken', token);
      setIsAuthenticated(true);
      setPassword('');
      toast.success('Login successful!');
      fetchData();
    } catch (error) {
      toast.error('Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken('');
    setIsAuthenticated(false);
    toast.success('Logged out');
  };

  const fetchData = async () => {
    fetchTestimonials();
    fetchFaqs();
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials?approved_only=false`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/faqs?active_only=false`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    }
  };

  // Testimonial CRUD
  const saveTestimonial = async (e) => {
    e.preventDefault();
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      
      if (editingTestimonial) {
        await axios.put(`${API_URL}/api/testimonials/${editingTestimonial.id}`, testimonialForm, { headers });
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API_URL}/api/testimonials`, testimonialForm, { headers });
        toast.success('Testimonial created!');
      }
      
      resetTestimonialForm();
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      await axios.delete(`${API_URL}/api/testimonials/${id}`, { headers });
      toast.success('Testimonial deleted!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const editTestimonial = (testimonial) => {
    setEditingTestimonial(testimonial);
    setTestimonialForm({
      name: testimonial.name,
      location: testimonial.location,
      rating: testimonial.rating,
      text: testimonial.text,
      service: testimonial.service,
      approved: testimonial.approved
    });
  };

  const resetTestimonialForm = () => {
    setTestimonialForm({
      name: '',
      location: '',
      rating: 5,
      text: '',
      service: '',
      approved: true
    });
    setEditingTestimonial(null);
  };

  // FAQ CRUD
  const saveFaq = async (e) => {
    e.preventDefault();
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      
      if (editingFaq) {
        await axios.put(`${API_URL}/api/faqs/${editingFaq.id}`, faqForm, { headers });
        toast.success('FAQ updated!');
      } else {
        await axios.post(`${API_URL}/api/faqs`, faqForm, { headers });
        toast.success('FAQ created!');
      }
      
      resetFaqForm();
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      await axios.delete(`${API_URL}/api/faqs/${id}`, { headers });
      toast.success('FAQ deleted!');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const editFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order,
      active: faq.active
    });
  };

  const resetFaqForm = () => {
    setFaqForm({
      question: '',
      answer: '',
      category: 'General',
      order: 0,
      active: true
    });
    setEditingFaq(null);
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" style={{ paddingTop: '80px' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-primary)' }}>
              <Lock className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2">Content Admin</h1>
          <p className="text-center mb-8 text-gray-600">Manage testimonials and FAQs</p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
                placeholder="Admin password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            <button
              type="submit"
              className="w-full py-3 rounded-lg text-white font-semibold"
              style={{ background: 'var(--accent-primary)' }}
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingTop: '80px', paddingBottom: '40px' }}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold">Content Management</h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`px-6 py-3 rounded-lg font-semibold ${activeTab === 'testimonials' ? 'bg-green-600 text-white' : 'bg-white'}`}
          >
            Testimonials ({testimonials.length})
          </button>
          <button
            onClick={() => setActiveTab('faqs')}
            className={`px-6 py-3 rounded-lg font-semibold ${activeTab === 'faqs' ? 'bg-green-600 text-white' : 'bg-white'}`}
          >
            FAQs ({faqs.length})
          </button>
        </div>

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div>
            {/* Form */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h2>
              <form onSubmit={saveTestimonial} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={testimonialForm.name}
                    onChange={(e) => setTestimonialForm({...testimonialForm, name: e.target.value})}
                    placeholder="Name"
                    className="px-4 py-2 border rounded"
                    required
                  />
                  <input
                    type="text"
                    value={testimonialForm.location}
                    onChange={(e) => setTestimonialForm({...testimonialForm, location: e.target.value})}
                    placeholder="Location"
                    className="px-4 py-2 border rounded"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={testimonialForm.service}
                    onChange={(e) => setTestimonialForm({...testimonialForm, service: e.target.value})}
                    placeholder="Service"
                    className="px-4 py-2 border rounded"
                    required
                  />
                  <select
                    value={testimonialForm.rating}
                    onChange={(e) => setTestimonialForm({...testimonialForm, rating: parseInt(e.target.value)})}
                    className="px-4 py-2 border rounded"
                  >
                    {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} Stars</option>)}
                  </select>
                </div>
                <textarea
                  value={testimonialForm.text}
                  onChange={(e) => setTestimonialForm({...testimonialForm, text: e.target.value})}
                  placeholder="Testimonial text"
                  className="w-full px-4 py-2 border rounded"
                  rows="4"
                  required
                />
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={testimonialForm.approved}
                    onChange={(e) => setTestimonialForm({...testimonialForm, approved: e.target.checked})}
                  />
                  Approved (visible on website)
                </label>
                <div className="flex gap-4">
                  <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded">
                    <Save className="w-4 h-4 inline mr-2" />
                    {editingTestimonial ? 'Update' : 'Create'}
                  </button>
                  {editingTestimonial && (
                    <button type="button" onClick={resetTestimonialForm} className="px-6 py-2 border rounded">
                      <X className="w-4 h-4 inline mr-2" />
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* List */}
            <div className="space-y-4">
              {testimonials.map(t => (
                <div key={t.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold">{t.name}</h3>
                        <span className="text-sm text-gray-600">- {t.location}</span>
                        {!t.approved && <span className="text-xs bg-yellow-100 px-2 py-1 rounded">Pending</span>}
                      </div>
                      <div className="text-yellow-500 mb-2">{'⭐'.repeat(t.rating)}</div>
                      <p className="text-gray-700 mb-2">{t.text}</p>
                      <p className="text-sm text-gray-500">Service: {t.service}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button onClick={() => editTestimonial(t)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                        <Edit className="w-5 h-5" />
                      </button>
                      <button onClick={() => deleteTestimonial(t.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQs Tab */}
        {activeTab === 'faqs' && (
          <div>
            {/* Form */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
              </h2>
              <form onSubmit={saveFaq} className="space-y-4">
                <input
                  type="text"
                  value={faqForm.question}
                  onChange={(e) => setFaqForm({...faqForm, question: e.target.value})}
                  placeholder="Question"
                  className="w-full px-4 py-2 border rounded"
                  required
                />
                <textarea
                  value={faqForm.answer}
                  onChange={(e) => setFaqForm({...faqForm, answer: e.target.value})}
                  placeholder="Answer"
                  className="w-full px-4 py-2 border rounded"
                  rows="4"
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={faqForm.category}
                    onChange={(e) => setFaqForm({...faqForm, category: e.target.value})}
                    placeholder="Category"
                    className="px-4 py-2 border rounded"
                  />
                  <input
                    type="number"
                    value={faqForm.order}
                    onChange={(e) => setFaqForm({...faqForm, order: parseInt(e.target.value)})}
                    placeholder="Display order"
                    className="px-4 py-2 border rounded"
                  />
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={faqForm.active}
                    onChange={(e) => setFaqForm({...faqForm, active: e.target.checked})}
                  />
                  Active (visible on website)
                </label>
                <div className="flex gap-4">
                  <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded">
                    <Save className="w-4 h-4 inline mr-2" />
                    {editingFaq ? 'Update' : 'Create'}
                  </button>
                  {editingFaq && (
                    <button type="button" onClick={resetFaqForm} className="px-6 py-2 border rounded">
                      <X className="w-4 h-4 inline mr-2" />
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* List */}
            <div className="space-y-4">
              {faqs.map(faq => (
                <div key={faq.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold">Q: {faq.question}</h3>
                        {!faq.active && <span className="text-xs bg-gray-100 px-2 py-1 rounded">Inactive</span>}
                        <span className="text-xs bg-blue-100 px-2 py-1 rounded">{faq.category}</span>
                        <span className="text-xs text-gray-500">Order: {faq.order}</span>
                      </div>
                      <p className="text-gray-700">A: {faq.answer}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button onClick={() => editFaq(faq)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                        <Edit className="w-5 h-5" />
                      </button>
                      <button onClick={() => deleteFaq(faq.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentAdmin;
